/**
 * Pricing-unit helpers — frontend mirror of PER_PERSON_PRICING_UNITS,
 * VEHICLE_PRICING_UNIT, and MAX_VEHICLE_UNITS in backend/providers/native.py,
 * and TIME_UNIT_PRICING_UNITS in backend/models/service.py. If those
 * constants change, update this file too.
 *
 * Rule:
 *   pricing_unit in {null, undefined, '', 'person'} (case-insensitive + trimmed)
 *     → multiply by guest count (per-person behaviour, today's default)
 *   pricing_unit === 'vehicle'
 *     → ceil(guest_count / max_capacity), hard-capped at MAX_VEHICLE_UNITS
 *   pricing_unit in {'hour', '30min', 'day'} (case-insensitive + trimmed)
 *     → the caller-supplied estimatedUnitCount (defaulting to 1 when omitted)
 *   any other unit (ride, pack, month, session, custom)
 *     → exactly one unit price regardless of guest count or estimatedUnitCount
 *
 * The backend is the pricing authority: NativeProvider.validate_before_booking
 * overwrites the client total with the server-computed value. Both sides must
 * mirror this rule so the displayed total matches what the server books.
 */
import { BillingBasis } from '../types';

/** Units that behave as per-person (multiply by guest_count). */
const PER_PERSON_PRICING_UNITS = new Set(['', 'person']);

/**
 * Unit that bills by vehicle count rather than guest count.
 * Mirrors VEHICLE_PRICING_UNIT in backend/providers/native.py.
 */
export const VEHICLE_PRICING_UNIT = 'vehicle';

/**
 * Hard cap on the number of vehicles a single booking can bill.
 * Mirrors MAX_VEHICLE_UNITS in backend/providers/native.py.
 */
export const MAX_VEHICLE_UNITS = 2;

/**
 * Units billed at rate x a guest-entered duration/count, gated so no other
 * unit is accidentally quantity-priced.
 * Mirrors TIME_UNIT_PRICING_UNITS in backend/models/service.py.
 */
const TIME_UNIT_PRICING_UNITS = new Set(['hour', '30min', 'day']);

/**
 * Returns true when pricing_unit indicates a per-person-like pricing model
 * (null, undefined, empty string, or 'person', case-insensitive).
 *
 * Mirrors PER_PERSON_PRICING_UNITS in backend/providers/native.py.
 */
export function isPerPersonUnit(unit: string | null | undefined): boolean {
  const normalised = (unit ?? '').trim().toLowerCase();
  return PER_PERSON_PRICING_UNITS.has(normalised);
}

/**
 * Returns true when pricing_unit indicates the vehicle-priced billing model
 * (bill by vehicle count, not guest count).
 *
 * Mirrors VEHICLE_PRICING_UNIT in backend/providers/native.py.
 */
export function isVehicleUnit(unit: string | null | undefined): boolean {
  const normalised = (unit ?? '').trim().toLowerCase();
  return normalised === VEHICLE_PRICING_UNIT;
}

/**
 * Returns true when pricing_unit indicates a time-unit pricing model
 * (billed by a guest-entered duration/count — hour, 30min, or day —
 * rather than guest count or a fixed single unit).
 *
 * Mirrors TIME_UNIT_PRICING_UNITS in backend/models/service.py.
 */
export function isTimeUnit(unit: string | null | undefined): boolean {
  const normalised = (unit ?? '').trim().toLowerCase();
  return TIME_UNIT_PRICING_UNITS.has(normalised);
}

/**
 * Returns the number of vehicles billable for a given party size and
 * per-vehicle seat capacity, hard-capped at MAX_VEHICLE_UNITS.
 *
 * Mirrors ceil(guest_count / max_capacity) capped at MAX_VEHICLE_UNITS in
 * NativeProvider.validate_before_booking (backend/providers/native.py).
 */
export function vehicleUnitCount(guests: number, maxCapacity: number | null | undefined): number {
  if (!guests || !maxCapacity) return 1;
  return Math.min(Math.ceil(guests / maxCapacity), MAX_VEHICLE_UNITS);
}

/**
 * Returns the number of units to multiply by for a booking:
 *   - per-person units → guest_count
 *   - vehicle units     → vehicleUnitCount(guests, maxCapacity), when maxCapacity is known
 *   - time units        → estimatedUnitCount (defaulting to 1 when omitted)
 *   - any other unit    → 1
 *
 * Use this instead of always multiplying by guests so that per-ride / per-hour /
 * per-pack services display and book a single unit price.
 *
 * Mirrors the units derivation in NativeProvider.validate_before_booking
 * (backend/providers/native.py).
 *
 * @param maxCapacity - seats per vehicle; only consulted for vehicle units.
 * @param estimatedUnitCount - booked duration/count; only consulted for
 *   time units (hour/30min/day). Ignored for every other pricing unit —
 *   the same scope guard the backend enforces via
 *   TIME_UNIT_PRICING_UNITS, keeping non-time units structurally immune
 *   to a client that sends the param.
 */
export function bookingUnitCount(
  unit: string | null | undefined,
  guests: number,
  maxCapacity?: number | null,
  estimatedUnitCount?: number,
): number {
  if (isPerPersonUnit(unit)) return guests;
  if (isVehicleUnit(unit) && maxCapacity) return vehicleUnitCount(guests, maxCapacity);
  if (isTimeUnit(unit)) return estimatedUnitCount || 1;
  return 1;
}

/**
 * Compute the display total price for a service booking.
 *
 * Extracted from the duplicated calculateTotalPrice functions in
 * ServiceBookingCard.tsx and MobileServiceBooking.tsx.
 *
 * @param pricePerUnit - service.price_per_person
 * @param unit         - service.pricing_unit
 * @param guests       - bookingDetails.guests
 * @param maxCapacity  - seats per vehicle; only consulted for vehicle units.
 * @returns total display price (0 if any input is missing)
 */
export function calculateTotalPrice(
  pricePerUnit: number | null | undefined,
  unit: string | null | undefined,
  guests: number,
  maxCapacity?: number | null,
): number {
  if (!pricePerUnit || !guests) return 0;
  return pricePerUnit * bookingUnitCount(unit, guests, maxCapacity);
}

/**
 * Compute the cart price for a service booking (uses final_price_per_person
 * which includes platform fee and vendor tax).
 *
 * Extracted from the duplicated calculateCartPrice functions in
 * ServiceBookingCard.tsx and MobileServiceBooking.tsx.
 *
 * @param finalPricePerUnit - service.final_price_per_person
 * @param unit              - service.pricing_unit
 * @param guests            - bookingDetails.guests
 * @param maxCapacity       - seats per vehicle; only consulted for vehicle units.
 * @param estimatedUnitCount - booked duration/count; only consulted for time
 *   units (hour/30min/day). See bookingUnitCount for the full rule.
 * @returns total cart price (0 if any input is missing)
 */
export function calculateCartPrice(
  finalPricePerUnit: number | null | undefined,
  unit: string | null | undefined,
  guests: number,
  maxCapacity?: number | null,
  estimatedUnitCount?: number,
): number {
  if (!finalPricePerUnit || !guests) return 0;
  return finalPricePerUnit * bookingUnitCount(unit, guests, maxCapacity, estimatedUnitCount);
}

/** Generic unit label when a booking carries no pricing-unit snapshot.
 *  Mirrors METERED_UNIT_LABEL_FALLBACK in backend/services/notification.py. */
export const UNIT_LABEL_FALLBACK = 'unit';

/**
 * Render a unit count with its pluralised unit label, e.g. `5 hours`.
 * Mirrors `_unit_count_label` in backend/services/notification.py so the
 * on-page disclosure and the emailed one read identically.
 */
export function formatUnitCount(count: number, unit: string | null | undefined): string {
  const label = unit || UNIT_LABEL_FALLBACK;
  return `${count} ${count === 1 ? label : `${label}s`}`;
}

const TIME_UNIT_PLURAL_NOUNS: Record<string, string> = {
  hour: 'hours',
  day: 'days',
  '30min': '30-minute blocks',
};

/**
 * Plural noun for a time unit's count (`hours`, `days`, `30-minute blocks`),
 * or null for a non-time unit.
 */
export function timeUnitPluralNoun(unit: string | null | undefined): string | null {
  return TIME_UNIT_PLURAL_NOUNS[(unit ?? '').trim().toLowerCase()] ?? null;
}

export interface TimeUnitStepperConfig {
  pricingUnit: string;
  isMetered: boolean;
  minUnits: number;
  maxUnits: number;
}

// Client-side default ceiling for the duration/count stepper. Used when an
// option sets no maximum of its own; tighter than the server bound to keep
// the stepper usable.
export const MAX_TIME_UNIT_COUNT = 100;

// The server's Field(ge=1, le=1000) on estimated_unit_count
// (backend/schemas/booking.py) is the authoritative ceiling.
const SERVER_MAX_TIME_UNIT_COUNT = 1000;

interface TimeUnitStepperRow {
  pricing_unit?: string | null;
  billing_basis?: BillingBasis | null;
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
}

/**
 * Stepper configuration for the bookable row (the selected option, or the
 * service itself when it has none). Null when the row is not time-priced.
 */
export function resolveTimeUnitStepper(
  row: TimeUnitStepperRow | null | undefined,
): TimeUnitStepperConfig | null {
  if (!row || !isTimeUnit(row.pricing_unit)) return null;
  const minUnits = Math.min(row.minimum_unit_count ?? 1, SERVER_MAX_TIME_UNIT_COUNT);
  const maxUnits = Math.min(
    row.maximum_unit_count ?? Math.max(MAX_TIME_UNIT_COUNT, minUnits),
    SERVER_MAX_TIME_UNIT_COUNT,
  );
  return {
    pricingUnit: (row.pricing_unit ?? '').trim().toLowerCase(),
    isMetered: row.billing_basis === 'METERED',
    minUnits,
    maxUnits,
  };
}

const TIME_UNIT_NOUNS: Record<string, { singular: string; plural: string }> = {
  hour: { singular: 'hour', plural: 'hours' },
  '30min': { singular: 'half-hour', plural: 'half-hours' },
  day: { singular: 'day', plural: 'days' },
};

/**
 * Singular/plural noun for a breakdown line's "x N <unit>" copy on a
 * time-unit service. Falls back to the raw unit for any other unit.
 */
export const timeUnitNoun = (unit: string | null | undefined, count: number): string => {
  const normalised = (unit ?? '').trim().toLowerCase();
  const noun = TIME_UNIT_NOUNS[normalised];
  if (!noun) return unit || '';
  return count === 1 ? noun.singular : noun.plural;
};

/**
 * Noun for a pricing unit in "{rate} per {unit}" copy: person by default,
 * "half-hour" for 30min, and any unit without a noun of its own (ride,
 * pack) as it is written.
 */
export const pricingUnitNoun = (unit: string | null | undefined): string => {
  if (isPerPersonUnit(unit)) return 'person';
  const normalised = (unit ?? '').trim().toLowerCase();
  return TIME_UNIT_NOUNS[normalised]?.singular ?? (unit ?? '').trim();
};
